import React, { useEffect, useState } from "react";
import { Modal, message, Tooltip } from "antd";
import { useAppDispatch, useAppSelector } from "../../modal/hooks";
import { getLink } from "../../services/questions/questions.service";
import { APIStatus } from "../../constants";
import { clearGetLink } from "../../services/questions/questions.slice";
import { Config } from "../../config";
import { axiosMultiPartConfig } from "../../utils/util.fns";
import axios from "axios";
import { Spinner, Share } from "../../plugins/icons";
import { InfoCircleOutlined } from "@ant-design/icons";
import { AdvancedLoader } from "../../components/advanced-loader";
import { CopyClipboardIcon, MailIcon } from "../../assets/svg";
import "./generate-link.scss";

interface GenerateLinkProps {
	disabled?: boolean;
}

export const GenerateLink: React.FunctionComponent<GenerateLinkProps> = ({ disabled }) => {
	const isNegativeMark = useAppSelector((store) => store.settings.isNegativeMark);
	const getLinkComplete = useAppSelector((store) => store.questions.getLinkComplete);
	const token = useAppSelector((store) => store.questions.token);
	const linkId = useAppSelector((store) => store.questions.linkId);
	const dispatch = useAppDispatch();
	const [loading, setLoading] = useState<boolean>(false);
	const link = `${Config.siteUrl}/${token}/registration`;
	const [file, setFile] = useState<File | null>(null);
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [isShowGenerateLink, setIsShowGenerateLink] = useState<boolean>(false);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	const handleCopyLink = () => {
		if (window.navigator.clipboard) {
			window.navigator.clipboard.writeText(link);
			message.success("Link copied to clipboard");
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];

		if (selectedFile) {
			// Check if the file is an Excel file
			const isExcelFile =
				selectedFile.type === "application/vnd.ms-excel" ||
				selectedFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
				selectedFile.name.endsWith(".xls") ||
				selectedFile.name.endsWith(".xlsx");

			if (isExcelFile) {
				setFile(selectedFile);
				setError(null); // Clear any previous errors
			} else {
				setError("Please upload a valid Excel file (.xls or .xlsx).");
			}
		}
	};

	const handleShareLink = async () => {
		if (!file) return;

		const formData = new FormData();
		formData.append("file", file);
		setUploading(true);

		try {
			const response = await axios.post(`${Config.apiSecureUrl}/exam/upload/${linkId}`, formData, {
				...axiosMultiPartConfig(),
				onUploadProgress: (progressEvent) => {
					if (progressEvent.total) {
						const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
						setProgress(percentCompleted);
					}
				},
			});

			console.log("Upload successful:", response.data);
			handleClear();
			// message.success("Link share successful");
		} catch (error) {
			console.error("Upload failed:", error);
			// message.error("Link share failed");
		} finally {
			setUploading(false);
		}
	};

	const handleCancel = () => {
		setIsModalOpen(false);
		setIsShowGenerateLink(false);
	};

	const handleModalOpen = () => {
		setIsModalOpen(true);
	};
	const handleClear = () => {
		const fileInput = document.getElementById("fileInput") as HTMLInputElement;
		if (fileInput) {
			fileInput.value = "";
		}
		setFile(null);
		setError(null);
		setUploading(false);
		setProgress(0);
	};

	useEffect(() => {
		if (getLinkComplete === APIStatus.FULFILLED) {
			setLoading(false);
			setIsShowGenerateLink(true);
			dispatch(clearGetLink());
		}
		if (getLinkComplete === APIStatus.REJECTED) {
			setLoading(false);
			dispatch(clearGetLink());
		}
	}, [getLinkComplete]);

	const handleGenerateLink = () => {
		setLoading(true);
		dispatch(getLink(isNegativeMark));
	};

	return (
		<>
			<button
				className="primary_border_btn min_height_36 font_16 min_width_175"
				onClick={handleModalOpen}
				disabled={disabled}
			>
				Generate Link
			</button>
			{isModalOpen ? (
				<Modal
					title={
						<div className="d-flex align-items-center">
							Examination Link
							<Tooltip title={`Negative marks ${isNegativeMark ? "enabled" : "disabled"}`}>
								<InfoCircleOutlined style={{ fontSize: 21, marginLeft: 8, cursor: "pointer", color: "#f15623" }} />
							</Tooltip>
						</div>
					}
					visible={true}
					className="generate_link_pop_up_screen"
					onCancel={handleCancel}
					closable={true}
					footer={null}
				>
					<div className="generate_link_box_content px-4 mb-2">
						{!isShowGenerateLink ? (
							<div className="button_container d-flex flex-column">
								<div className="font_regular text_color pl-1 min_height_85">
									<div className="font_16">
										Once the exam link is generated, no additional questions can be added for the next exam session.
									</div>
									<div className="font_16">Are you sure you want to generate the exam link?</div>
									<div className="d-flex align-items-center justify-content-end pr-3">
										<button
											className="secondary_border_btn font_15 min_height_34 min_width_150 mr-1"
											disabled={loading}
											onClick={handleCancel}
										>
											Cancel
										</button>
										<button
											className="primary_btn font_15 min_width_170 min_height_34 ml-2"
											disabled={loading}
											onClick={handleGenerateLink}
										>
											{loading ? <AdvancedLoader /> : <span>Generate Exam Link</span>}
										</button>
									</div>
								</div>
							</div>
						) : (
							<>
								<div className="generate_link_sub_header mb-1 pl-1">You may share the link to initiate the exam.</div>
								<div className="d-flex align-items-center w-100">
									<div className="generate_link text-break">{link}</div>
									<Tooltip title="Copy link" placement="top">
										<div
											className="button_container icon_button icon_container cursor_pointer"
											onClick={handleCopyLink}
										>
											{/* <button className="primary_border_btn font_16 min_width_120 ml-2" onClick={handleCopyLink}>
											Copye
										</button> */}
											<CopyClipboardIcon />
										</div>
									</Tooltip>
								</div>
								<div className="upload_csv px-1">
									<div className="upload_button">
										<div className="file_overlap">
											<input
												type="file"
												name="mailFile"
												className="uploadInput"
												id="fileInput"
												onChange={handleFileChange}
											/>
										</div>
										<div className="file_name">
											{uploading && <Spinner />}
											{error && (
												<p className="mt-2" style={{ color: "red" }}>
													{error}
												</p>
											)}
										</div>
										{(file || error) && !uploading && (
											<button onClick={handleClear} className="close_button">
												{" "}
												X{" "}
											</button>
										)}
									</div>
									<button
										disabled={error || !file || uploading ? true : false}
										className="primary_border_btn font_16 min_width_120 ml-2 send_button"
										onClick={handleShareLink}
									>
										<Share />
										Share
									</button>
								</div>
							</>
						)}
					</div>
				</Modal>
			) : (
				""
			)}
		</>
	);
};
